import React, { useEffect, useMemo, useState } from "react";
// Modal, Form, Alert, Spinner still come from react-bootstrap; native
// <button> elements replace the previous react-bootstrap Buttons so we can
// apply the modernised gradient/secondary styles without fighting Bootstrap's
// .btn class cascade.
import { Modal, Form, Alert, Spinner, Table } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import { fmtAmount } from "./format";

interface EnrollmentFormModalProps {
  show: boolean;
  onHide: () => void;
  mode: "edit" | "create";
  enrollmentId?: number;
  existingData?: {
    batchId: number | null;
    statusId: number | null;
    joinDateTime: string | null;
    studentCode: string | null;
    studentCode2: string | null;
    installmentTemplateId: number | null;
    remarks: string | null;
    lateralEntry: boolean;
    graduationDate: string | null;
    gradePoint: number | null;
    studentEnrollmentCategoryId: number | null;
    studentEnrollmentCategory2Id: number | null;
    schoolId: number | null;
    yearId: number | null;
    entryYearId: number | null;
    currentYearId: number | null;
    programId: number | null;
    courseTypeName?: string | null;
  };
  enquiryId?: number;
  onSuccess: (enrollmentId: number) => void;
}

const EnrollmentFormModal: React.FC<EnrollmentFormModalProps> = ({
  show,
  onHide,
  mode,
  enrollmentId,
  existingData,
  enquiryId,
  onSuccess,
}) => {
  const [lookups, setLookups] = useState<any>(null);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Form state
  const [batchId, setBatchId] = useState<string>("");
  const [statusId, setStatusId] = useState<string>("");
  const [joinDateTime, setJoinDateTime] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const [studentCode2, setStudentCode2] = useState("");
  const [installmentTemplateId, setInstallmentTemplateId] = useState<string>("");
  const [remarks, setRemarks] = useState("");
  const [lateralEntry, setLateralEntry] = useState(false);
  const [graduationDate, setGraduationDate] = useState("");
  const [gradePoint, setGradePoint] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [category2Id, setCategory2Id] = useState<string>("");
  const [schoolId, setSchoolId] = useState<string>("");
  const [yearId, setYearId] = useState<string>("");
  const [entryYearId, setEntryYearId] = useState<string>("");
  const [currentYearId, setCurrentYearId] = useState<string>("");
  const [programId, setProgramId] = useState<string>("");
  // Course is not shown in the form; it's inherited from the enquiry on create
  // and used only to filter eligible installment templates (course-based tenants).
  const [courseId, setCourseId] = useState<string>("");

  // Installment templates filtered by program/course + school + year (create
  // mode). Null until the first filtered fetch resolves; falls back to the full
  // lookups list so the dropdown is never empty while loading.
  const [filteredTemplates, setFilteredTemplates] = useState<any[] | null>(null);

  // Plan preview for the chosen installment template (edit mode) — mirrors the
  // Reconcile modal so the user sees the plan the template produces, and the
  // plan is rebuilt on save when the template changes.
  const [preview, setPreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [originalTemplateId, setOriginalTemplateId] = useState<string>("");

  const loadPreview = (tid: string) => {
    if (!tid) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    // Edit uses the enrollment-scoped preplan; create (no enrollment yet) uses
    // the template-only preview. Both return the same shape.
    const request =
      mode === "edit" && enrollmentId
        ? EnrollmentService.getPreplan(enrollmentId, Number(tid))
        : EnrollmentService.getTemplatePreview(Number(tid));
    request
      .then((res: any) => {
        if (res.data?.success) setPreview(res.data.data);
        else setPreviewError(res.data?.message || "Failed to load plan preview");
      })
      .catch((err: any) =>
        setPreviewError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load plan preview",
        ),
      )
      .finally(() => setPreviewLoading(false));
  };

  useEffect(() => {
    if (!show) return;
    setError(null);
    setSuccessMsg(null);
    setPreview(null);
    setPreviewError(null);
    setFilteredTemplates(null);
    setLookupsLoading(true);

    EnrollmentService.getLookups()
      .then((res: any) => {
        if (res.data?.success) {
          setLookups(res.data.data);
        } else {
          setError("Failed to load form data");
        }
      })
      .catch((err: any) => setError(err.message || "Failed to load form data"))
      .finally(() => setLookupsLoading(false));

    // Pre-fill form
    if (mode === "edit" && existingData) {
      setBatchId(existingData.batchId ? String(existingData.batchId) : "");
      setStatusId(existingData.statusId ? String(existingData.statusId) : "");
      setJoinDateTime(existingData.joinDateTime || "");
      setStudentCode(existingData.studentCode || "");
      setStudentCode2(existingData.studentCode2 || "");
      {
        const tpl = existingData.installmentTemplateId
          ? String(existingData.installmentTemplateId)
          : "";
        setInstallmentTemplateId(tpl);
        setOriginalTemplateId(tpl);
        // Show the current plan on open.
        if (tpl) loadPreview(tpl);
      }
      setRemarks(existingData.remarks || "");
      setLateralEntry(existingData.lateralEntry || false);
      setGraduationDate(existingData.graduationDate || "");
      setGradePoint(
        existingData.gradePoint != null ? String(existingData.gradePoint) : "",
      );
      setCategoryId(
        existingData.studentEnrollmentCategoryId
          ? String(existingData.studentEnrollmentCategoryId)
          : "",
      );
      setCategory2Id(
        existingData.studentEnrollmentCategory2Id
          ? String(existingData.studentEnrollmentCategory2Id)
          : "",
      );
      setSchoolId(existingData.schoolId ? String(existingData.schoolId) : "");
      setYearId(existingData.yearId ? String(existingData.yearId) : "");
      setEntryYearId(
        existingData.entryYearId ? String(existingData.entryYearId) : "",
      );
      setCurrentYearId(
        existingData.currentYearId ? String(existingData.currentYearId) : "",
      );
      setProgramId(
        existingData.programId ? String(existingData.programId) : "",
      );
      setCourseId("");
    } else if (mode === "create") {
      setBatchId(existingData?.batchId ? String(existingData.batchId) : "");
      setStatusId(existingData?.statusId ? String(existingData.statusId) : "");
      setJoinDateTime(existingData?.joinDateTime || new Date().toISOString().slice(0, 10));
      setStudentCode(existingData?.studentCode || "");
      setStudentCode2(existingData?.studentCode2 || "");
      {
        const tpl = existingData?.installmentTemplateId
          ? String(existingData.installmentTemplateId)
          : "";
        setInstallmentTemplateId(tpl);
        setOriginalTemplateId("");
        if (tpl) loadPreview(tpl);
      }
      setRemarks(existingData?.remarks || "");
      setLateralEntry(existingData?.lateralEntry || false);
      setGraduationDate(existingData?.graduationDate || "");
      setGradePoint(existingData?.gradePoint != null ? String(existingData.gradePoint) : "");
      setCategoryId(existingData?.studentEnrollmentCategoryId ? String(existingData.studentEnrollmentCategoryId) : "");
      setCategory2Id(existingData?.studentEnrollmentCategory2Id ? String(existingData.studentEnrollmentCategory2Id) : "");
      setSchoolId(existingData?.schoolId ? String(existingData.schoolId) : "");
      setYearId(existingData?.yearId ? String(existingData.yearId) : "");
      setEntryYearId("");
      setCurrentYearId("");
      setProgramId(existingData?.programId ? String(existingData.programId) : "");
      setCourseId("");

      // Pre-fill Centre / Year / Program (and hidden Course) from the source
      // enquiry so a new enrollment inherits the enquiry's context. Only fields
      // the enquiry actually carries are set; the user can still change them.
      if (enquiryId) {
        EnrollmentService.getEnquiryDefaults(enquiryId)
          .then((res: any) => {
            if (!res.data?.success) return;
            const d = res.data.data || {};
            if (d.schoolId != null) setSchoolId(String(d.schoolId));
            if (d.yearId != null) setYearId(String(d.yearId));
            if (d.programId != null) setProgramId(String(d.programId));
            if (d.courseId != null) setCourseId(String(d.courseId));
            // Tenant reuses the StudentCode for repeat enrollments: prefill the
            // student's existing code (server returns it only when the flag is on
            // and a prior enrollment exists). The user can still override it.
            if (d.studentCode) setStudentCode(String(d.studentCode));
          })
          .catch(() => {
            // Non-fatal: the form still opens with a blank/default context.
          });
      }
    } else {
      setCourseId("");
    }
  }, [show, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create mode: narrow the installment-template list to those eligible for the
  // chosen program/course + centre + year — the same filter the Reconcile flow
  // applies. Re-runs whenever any of those change.
  useEffect(() => {
    if (!show || mode !== "create" || !lookups) return;
    let cancelled = false;
    EnrollmentService.getReconcileTemplates({
      programId: programId ? Number(programId) : undefined,
      courseId: courseId ? Number(courseId) : undefined,
      schoolId: schoolId ? Number(schoolId) : undefined,
      yearId: yearId ? Number(yearId) : undefined,
    })
      .then((res: any) => {
        if (cancelled || !res.data?.success) return;
        const list = res.data.data.installmentTemplates || [];
        setFilteredTemplates(list);
        // Drop (and un-preview) a selected template no longer eligible.
        setInstallmentTemplateId((cur) => {
          if (cur && !list.some((t: any) => String(t.id) === cur)) {
            setPreview(null);
            setPreviewError(null);
            return "";
          }
          return cur;
        });
      })
      .catch(() => {
        if (!cancelled) setFilteredTemplates(null);
      });
    return () => {
      cancelled = true;
    };
  }, [show, mode, lookups, programId, courseId, schoolId, yearId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create mode: the Batch list is scoped to the chosen Centre + Year +
  // Grade/Course so the user only picks a batch that actually belongs to that
  // context. Centre/Year come from batch_master (schoolId/yearId); the
  // course/program link comes from batch_mapping (lookups.batchCourses). Edit
  // mode keeps the full list untouched.
  const filteredBatches = useMemo(() => {
    const all: any[] = lookups?.batches || [];
    if (!lookups || mode !== "create") return all;

    const isProgramBased = !!lookups.isProgramBased;
    const batchCourses: any[] = lookups.batchCourses || [];

    return all.filter((b: any) => {
      if (schoolId && String(b.schoolId) !== String(schoolId)) return false;
      if (yearId && String(b.yearId) !== String(yearId)) return false;

      // Grade/Course filter via batch_mapping — only applied when we have
      // mapping rows and a course/program is actually selected.
      if (batchCourses.length) {
        if (isProgramBased && programId) {
          const ok = batchCourses.some(
            (m) =>
              String(m.batchId) === String(b.id) &&
              String(m.programId) === String(programId),
          );
          if (!ok) return false;
        } else if (!isProgramBased && courseId) {
          const ok = batchCourses.some(
            (m) =>
              String(m.batchId) === String(b.id) &&
              String(m.courseId) === String(courseId),
          );
          if (!ok) return false;
        }
      }
      return true;
    });
  }, [lookups, mode, schoolId, yearId, courseId, programId]);

  // Drop a selected batch that no longer belongs to the filtered set (create
  // mode only) so we never submit a batch outside the chosen context.
  useEffect(() => {
    if (mode !== "create" || !batchId) return;
    if (!filteredBatches.some((b: any) => String(b.id) === String(batchId))) {
      setBatchId("");
    }
  }, [filteredBatches]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!batchId) {
      setError("Batch is required");
      return;
    }
    if (!statusId) {
      setError("Status is required");
      return;
    }
    if (!joinDateTime) {
      setError("Joining date is required");
      return;
    }

    const body: any = {
      batchId: Number(batchId),
      statusId: Number(statusId),
      joinDateTime,
      studentCode: studentCode || null,
      studentCode2: studentCode2 || null,
      installmentTemplateId: installmentTemplateId
        ? Number(installmentTemplateId)
        : null,
      remarks: remarks || null,
      lateralEntry,
      graduationDate: graduationDate || null,
      gradePoint: gradePoint ? Number(gradePoint) : null,
      studentEnrollmentCategoryId: categoryId ? Number(categoryId) : null,
      studentEnrollmentCategory2Id: category2Id ? Number(category2Id) : null,
      schoolId: schoolId ? Number(schoolId) : null,
      yearId: yearId ? Number(yearId) : null,
      programId: programId ? Number(programId) : null,
      // Course-based tenants: the selected Grade IS the course. Sent so the
      // backend can build the auto StudentCode ($schoolCode$courseCode$next).
      courseId: courseId ? Number(courseId) : null,
    };

    if (mode === "edit") {
      body.entryYearId = entryYearId ? Number(entryYearId) : null;
      body.currentYearId = currentYearId ? Number(currentYearId) : null;
    }

    setSubmitting(true);
    try {
      let res: any;
      if (mode === "edit" && enrollmentId) {
        res = await EnrollmentService.updateEnrollment(enrollmentId, body);
      } else {
        body.enquiryId = enquiryId;
        res = await EnrollmentService.createEnrollment(body);
      }

      if (res.data?.success) {
        const eid = res.data.data.enrollmentId;

        // If the installment template changed in edit mode, rebuild the plan —
        // same as Reconcile → For Plan (typeId 2).
        if (
          mode === "edit" &&
          enrollmentId &&
          installmentTemplateId &&
          installmentTemplateId !== originalTemplateId
        ) {
          try {
            const rec: any = await EnrollmentService.reconcilePlan(enrollmentId, {
              typeId: 2,
              installmentTemplateId: Number(installmentTemplateId),
            });
            if (!rec.data?.success) {
              setError(
                "Enrollment saved, but the plan could not be updated: " +
                  (rec.data?.message || "unknown error"),
              );
              setSubmitting(false);
              return;
            }
          } catch (recErr: any) {
            setError(
              "Enrollment saved, but the plan could not be updated: " +
                (recErr.response?.data?.message ||
                  recErr.message ||
                  "unknown error"),
            );
            setSubmitting(false);
            return;
          }
        }

        setSuccessMsg(
          mode === "edit"
            ? installmentTemplateId !== originalTemplateId
              ? "Enrollment updated and plan rebuilt successfully"
              : "Enrollment updated successfully"
            : `Enrollment created (ID: ${eid})`,
        );
        onSuccess(eid);
      } else {
        setError(res.data?.message || "Operation failed");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Operation failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* Shared label / control / button styles — match the modernised
     student-search toolbar so the New Enrollment modal feels native to
     the page that launches it. Inline styles beat Bootstrap's class
     cascade, so we don't need to touch any external CSS. */
  const labelStyle: React.CSSProperties = {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "var(--text-body)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 4,
    display: "block",
  };
  const reqMarker: React.CSSProperties = {
    color: "var(--status-danger)",
    marginLeft: 3,
    fontWeight: 700,
  };
  const controlStyle: React.CSSProperties = {
    height: 36,
    padding: "0 10px",
    fontSize: "0.85rem",
    borderRadius: 6,
    border: "1px solid var(--border-strong)",
    background: "var(--surface-card)",
    color: "var(--text-strong)",
    width: "100%",
    boxSizing: "border-box",
  };
  const textareaStyle: React.CSSProperties = {
    ...controlStyle,
    height: "auto",
    padding: "8px 10px",
    minHeight: 64,
    resize: "vertical",
  };
  const sectionRowStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginBottom: 12,
  };
  const primaryBtnStyle: React.CSSProperties = {
    background: "var(--gradient-brand)",
    color: "var(--text-on-brand)",
    border: "none",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
  };
  const secondaryBtnStyle: React.CSSProperties = {
    background: "var(--text-muted)",
    color: "var(--text-on-brand)",
    border: "none",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  };
  // Reference unused style vars so TS doesn't strip them when the modal is
  // rendered in modes that skip some sections.
  void sectionRowStyle;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          background: "var(--surface-page)",
          borderBottom: "1px solid var(--border-default)",
          padding: "14px 20px",
        }}
      >
        <Modal.Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text-strong)" }}>
            {mode === "edit" ? "Edit Enrollment" : "New Enrollment"}
          </span>
          {mode === "create" && enquiryId && (
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 12,
                background: "var(--brand-primary-subtle)",
                color: "var(--brand-primary-hover)",
                border: "1px solid var(--brand-primary-subtle)",
                letterSpacing: "0.04em",
              }}
            >
              ENQUIRY #{enquiryId}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: "0.85rem", padding: 20, background: "var(--surface-card)" }}>
        {error && (
          <Alert variant="danger" style={{ fontSize: 12 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert variant="success" style={{ fontSize: 12 }}>
            {successMsg}
          </Alert>
        )}

        {lookupsLoading ? (
          <div style={{ textAlign: "center", padding: 30 }}>
            <Spinner role="status" animation="border" size="sm" /> Loading form data...
          </div>
        ) : lookups ? (
          (() => {
            const isProgramBased = !!lookups.isProgramBased;
            // "Grade / Program" adapts to the tenant's system type.
            const gradeProgramLabel = isProgramBased ? "Program" : "Grade";
            const statusName =
              lookups.statuses?.find(
                (s: any) => String(s.id) === String(statusId),
              )?.name || "—";
            // Create mode uses the enquiry/context-filtered template list; edit
            // mode keeps the full list. Fall back to the full list while the
            // filtered fetch is still in flight so the dropdown is never empty.
            const templateList =
              mode === "create"
                ? filteredTemplates ?? lookups.installmentTemplates
                : lookups.installmentTemplates;
            return (
              <>
                {/* Row 1: Centre, Course / Program */}
                <div style={sectionRowStyle}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Centre</Form.Label>
                    <Form.Control
                      as="select"
                      value={schoolId}
                      onChange={(e) => setSchoolId(e.target.value)}
                      style={controlStyle}
                    >
                      <option value="">-- Select Centre --</option>
                      {lookups.schools.map((s: any) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={labelStyle}>{gradeProgramLabel}</Form.Label>
                    {isProgramBased ? (
                      // Program-based tenants: choose an academic program. Default
                      // comes from student_enquiry_program_mapping (enquiry defaults).
                      <Form.Control
                        as="select"
                        value={programId}
                        onChange={(e) => setProgramId(e.target.value)}
                        style={controlStyle}
                      >
                        <option value="">-- Select {gradeProgramLabel} --</option>
                        {lookups.programs?.map((p: any) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Form.Control>
                    ) : (
                      // Course-based tenants: the "Grade" IS the course. Populate
                      // from the course list; default comes from
                      // enquiry_course_mapping (enquiry defaults).
                      <Form.Control
                        as="select"
                        value={courseId}
                        onChange={(e) => setCourseId(e.target.value)}
                        style={controlStyle}
                      >
                        <option value="">-- Select {gradeProgramLabel} --</option>
                        {(lookups.courses || []).map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </Form.Control>
                    )}
                  </Form.Group>
                </div>

                {/* Course Type — program-based systems only (read-only; from the
                    program's enquiry-type-2 restriction, e.g. "core"). */}
                {isProgramBased && (
                  <div style={sectionRowStyle}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Course Type</Form.Label>
                      <div
                        style={{
                          ...controlStyle,
                          display: "flex",
                          alignItems: "center",
                          background: "var(--surface-page)",
                          color: "var(--text-muted)",
                          cursor: "not-allowed",
                        }}
                        title="Course Type is derived from the program"
                      >
                        {existingData?.courseTypeName || "—"}
                      </div>
                    </Form.Group>
                    <div />
                  </div>
                )}

                {/* Row 2: Year, Batch */}
                <div style={sectionRowStyle}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Year</Form.Label>
                    <Form.Control
                      as="select"
                      value={yearId}
                      onChange={(e) => setYearId(e.target.value)}
                      style={controlStyle}
                    >
                      <option value="">-- Select Year --</option>
                      {lookups.years.map((y: any) => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Batch <span style={reqMarker}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={batchId}
                      onChange={(e) => setBatchId(e.target.value)}
                      style={controlStyle}
                    >
                      <option value="">-- Select Batch --</option>
                      {filteredBatches.map((b: any) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>

                {/* Row 3: Student Code, Status (view-only in edit) */}
                <div style={sectionRowStyle}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Student Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={studentCode}
                      onChange={(e) => setStudentCode(e.target.value)}
                      placeholder={mode === "create" ? "Auto-generated" : ""}
                      style={controlStyle}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Status {mode === "create" && <span style={reqMarker}>*</span>}
                    </Form.Label>
                    {mode === "edit" ? (
                      // View mode — status changes go through the dedicated flow.
                      <div
                        style={{
                          ...controlStyle,
                          display: "flex",
                          alignItems: "center",
                          background: "var(--surface-page)",
                          color: "var(--text-muted)",
                          cursor: "not-allowed",
                        }}
                        title="Status is read-only here"
                      >
                        {statusName}
                      </div>
                    ) : (
                      <Form.Control
                        as="select"
                        value={statusId}
                        onChange={(e) => setStatusId(e.target.value)}
                        style={controlStyle}
                      >
                        <option value="">-- Select Status --</option>
                        {lookups.statuses.map((s: any) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </Form.Control>
                    )}
                  </Form.Group>
                </div>

                {/* Row 4: Enrollment Category, Category 2 */}
                <div style={sectionRowStyle}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Enrollment Category</Form.Label>
                    <Form.Control
                      as="select"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                      style={controlStyle}
                    >
                      <option value="">-- None --</option>
                      {lookups.enrollmentCategories.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Category 2</Form.Label>
                    <Form.Control
                      as="select"
                      value={category2Id}
                      onChange={(e) => setCategory2Id(e.target.value)}
                      style={controlStyle}
                    >
                      <option value="">-- None --</option>
                      {lookups.enrollmentCategories2.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>

                {/* Row 5: Joining Date, Installment Template */}
                <div style={sectionRowStyle}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>
                      Joining Date <span style={reqMarker}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={joinDateTime}
                      onChange={(e) => setJoinDateTime(e.target.value)}
                      style={controlStyle}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Installment Template</Form.Label>
                    <Form.Control
                      as="select"
                      value={installmentTemplateId}
                      onChange={(e) => {
                        setInstallmentTemplateId(e.target.value);
                        loadPreview(e.target.value);
                      }}
                      style={controlStyle}
                    >
                      <option value="">-- None --</option>
                      {templateList.map((t: any) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </div>

                {/* Plan preview for the chosen installment template */}
                {(previewLoading || preview || previewError) && (
                    <div style={{ marginBottom: 12 }}>
                      <Form.Label style={labelStyle}>Plan Preview</Form.Label>
                      {previewLoading ? (
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          <Spinner role="status" animation="border" size="sm" />{" "}
                          Loading plan...
                        </div>
                      ) : previewError ? (
                        <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
                          {previewError}
                        </Alert>
                      ) : preview ? (
                        <>
                          {mode === "edit" &&
                            installmentTemplateId !== originalTemplateId && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "var(--status-warning)",
                                  marginBottom: 6,
                                  fontWeight: 600,
                                }}
                              >
                                Changing the template will rebuild the plan when
                                you save.
                              </div>
                            )}
                          <div style={{ overflowX: "auto" }}>
                            <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Installment</th>
                                  {preview.heads.map((h: any) => (
                                    <th key={h.id} style={{ textAlign: "right" }}>
                                      {h.name}
                                    </th>
                                  ))}
                                  <th style={{ textAlign: "right" }}>Plan Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview.installments.map((inst: any) => (
                                  <tr key={inst.installmentId}>
                                    <td style={{ whiteSpace: "nowrap" }}>
                                      {inst.installmentDate}
                                    </td>
                                    <td>{inst.installmentName}</td>
                                    {preview.heads.map((h: any) => (
                                      <td key={h.id} style={{ textAlign: "right" }}>
                                        {inst.headAmounts[h.id] != null
                                          ? fmtAmount(inst.headAmounts[h.id])
                                          : "-"}
                                      </td>
                                    ))}
                                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                                      {fmtAmount(inst.planAmount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td
                                    colSpan={2 + preview.heads.length}
                                    style={{ fontWeight: 700 }}
                                  >
                                    Total
                                  </td>
                                  <td style={{ textAlign: "right", fontWeight: 700 }}>
                                    {fmtAmount(preview.totalPlanAmount)}
                                  </td>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        </>
                      ) : null}
                    </div>
                  )}

                {/* Entry Year, Current Year — edit mode, program-based systems only */}
                {mode === "edit" && isProgramBased && (
                  <div style={sectionRowStyle}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Entry Year</Form.Label>
                      <Form.Control
                        as="select"
                        value={entryYearId}
                        onChange={(e) => setEntryYearId(e.target.value)}
                        style={controlStyle}
                      >
                        <option value="">-- Select --</option>
                        {lookups.years.map((y: any) => (
                          <option key={y.id} value={y.id}>
                            {y.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Current Year</Form.Label>
                      <Form.Control
                        as="select"
                        value={currentYearId}
                        onChange={(e) => setCurrentYearId(e.target.value)}
                        style={controlStyle}
                      >
                        <option value="">-- Select --</option>
                        {lookups.years.map((y: any) => (
                          <option key={y.id} value={y.id}>
                            {y.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </div>
                )}

                {/* Graduation Date / Grade Point — program-based systems only */}
                {isProgramBased && (
                  <div style={sectionRowStyle}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Graduation Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={graduationDate}
                        onChange={(e) => setGraduationDate(e.target.value)}
                        style={controlStyle}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Grade Point</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={gradePoint}
                        onChange={(e) => setGradePoint(e.target.value)}
                        style={controlStyle}
                      />
                    </Form.Group>
                  </div>
                )}

                {/* Lateral Entry — program-based systems only */}
                {isProgramBased && (
                  <div style={{ marginBottom: 12 }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        height: 36,
                        padding: "0 12px",
                        background: "var(--surface-page)",
                        border: "1px solid var(--border-default)",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: "0.82rem",
                        color: "var(--text-strong)",
                        fontWeight: 500,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={lateralEntry}
                        onChange={(e) => setLateralEntry(e.target.checked)}
                        style={{ margin: 0, cursor: "pointer" }}
                      />
                      Lateral Entry
                    </label>
                  </div>
                )}

                {/* Remarks */}
                <Form.Group className="mb-2">
                  <Form.Label style={labelStyle}>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    style={textareaStyle}
                  />
                </Form.Group>
              </>
            );
          })()
        ) : null}
      </Modal.Body>
      <Modal.Footer
        style={{
          background: "var(--surface-page)",
          borderTop: "1px solid var(--border-default)",
          padding: "12px 20px",
          gap: 8,
        }}
      >
        {successMsg ? (
          <button type="button" onClick={onHide} style={secondaryBtnStyle}>
            Close
          </button>
        ) : (
          <>
            <button type="button" onClick={onHide} style={secondaryBtnStyle}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || lookupsLoading}
              style={{
                ...primaryBtnStyle,
                opacity: submitting || lookupsLoading ? 0.7 : 1,
                cursor: submitting || lookupsLoading ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {submitting ? (
                <>
                  <Spinner role="status" animation="border" size="sm" />
                  Saving…
                </>
              ) : mode === "edit" ? (
                "Save Changes"
              ) : (
                "Create Enrollment"
              )}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EnrollmentFormModal;
